import { Action, Thunk, action, thunk } from "easy-peasy";
import { post, get, del, put } from "requests";
import { downloadWithFileName, download } from "utils/FileDownloadUtils";

export interface FileUploadModel {
  mediaLoading: boolean;
  mediaError: string | null;
  uploadedSuccessfully: boolean;
  mediaAttachments: any[];
  fileCategories: any[];
  setMediaLoading: Action<FileUploadModel, boolean>;
  setMediaError: Action<FileUploadModel, string | null>;
  setUploadedSuccessfully: Action<FileUploadModel, boolean>;
  setMediaAttachments: Action<FileUploadModel, any[]>;
  setFileCategories: Action<FileUploadModel, any[]>;
  uploadFile: Thunk<
    FileUploadModel,
    {
      file: File;
      tenantId: number;
      mediaToken: string;
      dealApplicationId: number;
      description: string;
      category: string;
    }
  >;
  getMediaAttachments: Thunk<FileUploadModel, { dealApplicationId: number }>;
  getAllMerchantMediaAttachments: Thunk<FileUploadModel, { merchantId: number }>;
  deleteMediaAttachment: Thunk<FileUploadModel, { media: any }>;
  getAllFileCategories: Thunk<FileUploadModel>;
  updateFileDetails: Thunk<FileUploadModel, { request: any }>;
  downloadSingleFile: Thunk<FileUploadModel, { file: any }>;
  downloadSelectedDocuments: Thunk<FileUploadModel, { params: any }>;
}

const fileUploadModel: FileUploadModel = {
  mediaLoading: false,
  mediaError: null,
  uploadedSuccessfully: false,
  mediaAttachments: [],
  fileCategories: [],

  setFileCategories: action((state, data) => {
    state.fileCategories = data;
  }),

  setMediaLoading: action((state, loading) => {
    state.mediaLoading = loading;
  }),

  setMediaError: action((state, error) => {
    state.mediaError = error;
  }),

  setUploadedSuccessfully: action((state, success) => {
    state.uploadedSuccessfully = success;
  }),

  setMediaAttachments: action((state, attachments) => {
    state.mediaAttachments = attachments;
  }),

  uploadFile: thunk(
    async (actions, { file, tenantId, mediaToken, dealApplicationId, description, category }) => {
      actions.setMediaLoading(true);
      actions.setMediaError(null);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("tenantId", tenantId.toString());
      formData.append("mediaToken", mediaToken);
      formData.append("dealApplicationId", dealApplicationId.toString());
      formData.append("description", description);
      formData.append("category", category);

      try {
        const response = await post("/uploadDealApplicationMedia", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (response.data === "true") {
          actions.setUploadedSuccessfully(true);
        } else {
          actions.setMediaError("Unexpected response from server.");
        }
      } catch (error: any) {
        actions.setMediaError("Error uploading file: " + error.message);
        throw error;
      } finally {
        actions.setMediaLoading(false);
      }
    }
  ),

  getMediaAttachments: thunk(async (actions, { dealApplicationId }) => {
    actions.setMediaLoading(true);
    actions.setMediaError(null);

    try {
      const response = await post("/applicationMediaAttachments", { dealApplicationId });
      if (response && Array.isArray(response)) {
        actions.setMediaAttachments(response);
      } else {
        actions.setMediaError("Unexpected response format for media attachments.");
      }
    } catch (error: any) {
      actions.setMediaError("Error fetching media attachments: " + error.message);
      throw error;
    } finally {
      actions.setMediaLoading(false);
    }
  }),

  getAllMerchantMediaAttachments: thunk(async (actions, merchantId) => {
    actions.setMediaLoading(true);
    actions.setMediaError(null);

    try {
      const response = await post(`/fetchAllAvailableMerchantFiles`, { partyId: merchantId });

      if (response && Array.isArray(response)) {
        actions.setMediaAttachments(response);
      } else {
        actions.setMediaError("Unexpected response format for merchant media attachments.");
      }
    } catch (error: any) {
      actions.setMediaError("Error fetching merchant media attachments: " + error.message);
      throw error;
    } finally {
      actions.setMediaLoading(false);
    }
  }),

  deleteMediaAttachment: thunk(async (actions, media: any) => {
    actions.setMediaLoading(true);
    actions.setMediaError(null);
    try {
      await del(`/${media.mediaThrough}/${media.dealApplicationAttachmentId}`, { baseURL: "/" });
      await del(`/mediaAttachments/${media.id}`, { baseURL: "/" });
    } catch (error: any) {
      actions.setMediaError("Error Deleting Media: " + error.message);
      throw error;
    } finally {
      actions.setMediaLoading(false);
    }
  }),

  getAllFileCategories: thunk(async (actions) => {
    actions.setMediaLoading(true);
    actions.setMediaError(null);

    try {
      const response = await get(`/fileCategories`);

      if (response && Array.isArray(response)) {
        actions.setFileCategories(response);
      }
    } catch (error: any) {
      throw error;
    } finally {
      actions.setMediaLoading(false);
    }
  }),

  updateFileDetails: thunk(async (actions, request: any) => {
    actions.setMediaLoading(true);
    actions.setMediaError(null);

    try {
      await put(`/updateMediaAttachment`, request);
    } catch (error: any) {
      actions.setMediaError("Error updating file details: " + error.message);
      throw error;
    } finally {
      actions.setMediaLoading(false);
    }
  }),

  downloadSingleFile: thunk(async (actions, file: any) => {
    actions.setMediaLoading(true);
    actions.setMediaError(null);
    const downloadURL = file.s3Url;

    if (downloadURL) {
      downloadWithFileName(downloadURL, file.name);
    } else {
      actions.setMediaError("Unable to Download File");
    }

    actions.setMediaLoading(false);
  }),

  downloadSelectedDocuments: thunk(async (actions, params: any) => {
    actions.setMediaLoading(true);
    actions.setMediaError(null);

    try {
      const response = await post("createZipWithMediaAttachments", params);

      if (response.fileName) {
        download(response.fileName);
      } else {
        actions.setMediaError("No file to download");
      }
    } catch (error: any) {
      actions.setMediaError("Error downloading files: " + error.message);
    } finally {
      actions.setMediaLoading(false);
    }
  }),
};

export default fileUploadModel;
